// src/pages/VerifyEmail.jsx
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { setAccessToken } from "../api";
import { afTrack, trackSignup } from "../analytics/appsflyer";

// Keep the post-verification destination within SatStacker.
function safeInAppRedirect(value) {
  if (
    typeof value !== "string" ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    /[\\\r\n]/.test(value)
  ) return null;
  return value;
}

export default function VerifyEmail({
  setToken,
  fetchUserInfo,
}) {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [msg, setMsg] = useState("Verifying…");
  const startedRef = useRef(false);

  const verificationToken =
    params.get("token") || "";

  // The verification link may open in the same browser as signup, where the
  // original intent is stored. Keep the existing Smart Timing destination as
  // the fallback for users verifying from a different device/browser.
  const afterVerifyPath = (() => {
    const fromUrl = safeInAppRedirect(params.get("redirect"));
    if (fromUrl) return fromUrl;
    try {
      const saved = JSON.parse(
        localStorage.getItem("satstacker_signup_intent") || "null"
      );
      if (saved?.intent === "smart_timing_trial") {
        return safeInAppRedirect(saved.return_to) || "/plans?smartTiming=1";
      }
    } catch {}
    return "/plans?smartTiming=1";
  })();

  const loginDestination =
    `/login?redirect=${encodeURIComponent(afterVerifyPath)}`;

  useEffect(() => {
    // Prevent React/dev double-effects from consuming
    // a one-time verification token twice.
    if (startedRef.current) return;
    startedRef.current = true;

    async function verify() {
      if (!verificationToken) {
        setMsg(
          "Verification failed. The link is missing its token."
        );
        return;
      }

      try {
        const { data } = await api.get(
          "/verify-email",
          {
            params: {
              token: verificationToken,
            },
          }
        );

        const accessToken =
          data?.access_token;

        if (!accessToken) {
          throw new Error(
            "Verification succeeded without an access token."
          );
        }

        // WEB: access token stays in memory.
        // NATIVE: existing api.ts behavior also preserves it
        // in native local storage.
        setAccessToken(accessToken);

        if (setToken) {
          setToken(accessToken);
        }

        // The backend has now committed is_verified=True and burned the
        // one-time token. Count one confirmed email conversion, not the
        // earlier anonymous /users response. Never include tokens/emails.
        // Use attribution returned from the DB: verification may happen on
        // another device with no paid-traffic localStorage.
        const conversionParams = {
          method: "email",
          ...(data?.acquisition_source
            ? { acquisition_source: data.acquisition_source }
            : {}),
          ...(data?.acquisition_campaign
            ? { acquisition_campaign: data.acquisition_campaign }
            : {}),
        };

        // Analytics must never prevent a verified user from continuing.
        await Promise.allSettled([
          afTrack("signup_success", conversionParams),
          trackSignup("email"),
        ]);

        if (fetchUserInfo) {
          try {
            await fetchUserInfo(accessToken);
          } catch (loadError) {
            console.error(
              "Email confirmed, but account loading failed",
              loadError
            );
            setMsg("Email confirmed! Please sign in to continue.");
            return;
          }
        }

        setMsg(
          afterVerifyPath.startsWith("/plans")
            ? "Email confirmed! Opening Smart Timing…"
            : "Email confirmed! Continuing…"
        );

        navigate(afterVerifyPath, { replace: true });
      } catch (err) {
        console.error(
          "Email verification failed",
          err
        );

        setMsg(
          "Verification failed. Link may be expired."
        );
      }
    }

    verify();
  }, [
    verificationToken,
    navigate,
    setToken,
    fetchUserInfo,
    afterVerifyPath,
  ]);

  return (
    <div
      style={{
        textAlign: "center",
        marginTop: "6rem",
      }}
    >
      <p>{msg}</p>

      {(msg.startsWith("Verification failed") ||
        msg.startsWith("Email confirmed! Please sign in")) && (
        <p>
          <Link to={loginDestination}>
            Go to login
          </Link>
        </p>
      )}
    </div>
  );
}
